import React from 'react';
import {useIntl} from 'react-intl';
import {Dropdown} from '@/web-components';
import {InvoiceContact} from '../useInvoiceClientContacts';

// Sentinel id for the non-selectable "no contacts" row shown when the selected client has no
// contacts. Lets the dropdown still open on an empty collection and surfaces an empty-state
// message - mirrors ClientPrimaryContactDropdown (IGNITE-490).
const EMPTY_STATE_KEY = '__no_contacts__';

interface InvoiceClientContactDropdownProps {
	contacts: InvoiceContact[];
	loading: boolean;
	value: InvoiceContact | null;
	onChange: (contact: InvoiceContact | null) => void;
	dataCy?: string;
}

// Purely presentational and controlled (value/onChange) - per docs/spec/client-contact/003-invoice-contact.md
// "Component" section, all fetching/caching/selection-map ownership lives in useInvoiceClientContacts,
// consumed by the caller (create_invoice_modal_v2.js and its children), not by this component.
//
// Uses the same compact `Dropdown`/`Dropdown.SingleText` (web-components) as the invoice list's
// Status column (InvoicesTable.js) rather than the design-system ComboBox, so this field renders
// as the same single-pill, fixed-width control instead of the wider input+trigger-button combo.
export const InvoiceClientContactDropdown = ({
	contacts,
	loading,
	value,
	onChange,
	dataCy,
}: InvoiceClientContactDropdownProps) => {
	const {formatMessage} = useIntl();

	const isEmpty = !loading && contacts.length === 0;

	const handleSelect = (selectedValues: string[]) => {
		const [key] = selectedValues;
		if (!key || key === EMPTY_STATE_KEY) {
			onChange(null);
			return;
		}
		const found = contacts.find(c => c.id === key) ?? null;
		onChange(found);
	};

	const handleRemove = () => {
		onChange(null);
	};

	return (
		<Dropdown
			size="large"
			width={128}
			maxHeight={200}
			disabled={loading}
			usePortal
			isClearable
			dropdownAlignment={'center'}
			name={loading ? formatMessage({id: 'common.loading'}) : formatMessage({id: 'project_settings.select_contact'})}
			selectedItems={value ? [value.id] : []}
			onSelect={handleSelect}
			onRemove={handleRemove}
			cy={dataCy}
		>
			{isEmpty ? (
				<Dropdown.SingleText value={EMPTY_STATE_KEY} searchString="" disabled>
					{formatMessage({id: 'clients.client-contacts-empty-state'})}
				</Dropdown.SingleText>
			) : (
				contacts.map(contact => (
					<Dropdown.SingleText key={contact.id} value={contact.id} searchString={contact.name}>
						{contact.name}
					</Dropdown.SingleText>
				))
			)}
		</Dropdown>
	);
};
